import { useControls } from 'leva';
import { MUSIC_TRACKS } from '../../audio/musicTracks';
import {
  $audioConfig,
  $devConfig,
  updateAudioConfig,
  updateDevConfig,
} from '../../state/appConfig';

type ChangeContext = { initial?: boolean };

/** Leva fires `onChange` once on mount too; only a user edit is written back. */
const onEdit =
  <T>(apply: (value: T) => void) =>
  (value: T, _path: string, ctx: ChangeContext): void => {
    if (!ctx.initial) apply(value);
  };

/**
 * "App" block — the app-wide settings the legacy `AppConfigPanel` held and SDK
 * scenes had no panel for: sound, the assistant token, the mock POI-artifact
 * buttons and the preloader skip. All persist in `$appConfig`. Built once from
 * `getState()`: live deps would rebuild the folder under a dragged slider.
 */
export const useAppControls = (): void => {
  useControls(
    'App',
    () => {
      const audio = $audioConfig.getState();
      const dev = $devConfig.getState();
      return {
        'Sound On': {
          value: audio.soundOn,
          onChange: onEdit((soundOn: boolean) => updateAudioConfig({ soundOn })),
        },
        'Music On': {
          value: audio.musicOn,
          onChange: onEdit((musicOn: boolean) => updateAudioConfig({ musicOn })),
        },
        'Music Track': {
          options: Object.fromEntries(MUSIC_TRACKS.map((track) => [track.label, track.id])),
          value: audio.musicTrackId,
          onChange: onEdit((musicTrackId: string) => updateAudioConfig({ musicTrackId })),
        },
        'Music Volume': {
          value: audio.musicVolume,
          min: 0,
          max: 1,
          step: 0.01,
          onChange: onEdit((musicVolume: number) => updateAudioConfig({ musicVolume })),
        },
        'Assistant API Token': {
          value: dev.assistantApiToken,
          hint: 'Bearer token for assistant requests (optional)',
          onChange: onEdit((assistantApiToken: string) => updateDevConfig({ assistantApiToken })),
        },
        'Mock POI Artifacts': {
          value: dev.mockPoiArtifactButtons,
          hint: '[TEMP] Кнопки входа в POI-артефакт в подсказках чата.',
          onChange: onEdit((mockPoiArtifactButtons: boolean) =>
            updateDevConfig({ mockPoiArtifactButtons }),
          ),
        },
        'Skip Preloader Animation': {
          value: dev.skipPreloaderAnimation,
          hint: 'Без облёта и флайовера, reveal играет. Следующий вход в сцену.',
          onChange: onEdit((skipPreloaderAnimation: boolean) =>
            updateDevConfig({ skipPreloaderAnimation }),
          ),
        },
      };
    },
    [],
  );
};
